import { gql } from "@apollo/client";

export const GET_SCHOOL_CLASSES = gql`
  query (
    $usersOnly: Boolean
    $school: ID
    $showAllClasses: Boolean
    $searchTerm: String
    $educationLevel: ID
    $yearGroup: ID
    $subjects: [ID]
    $academicYear: ID
    $first: Int
    $after: String
  ) {
    schoolClasses(
      usersOnly: $usersOnly
      school: $school
      showAllClasses: $showAllClasses
      searchTerm: $searchTerm
      educationLevel: $educationLevel
      yearGroup: $yearGroup
      subjects: $subjects
      academicYear: $academicYear
      first: $first
      after: $after
    ) {
      edges {
        cursor
        node {
          id
          name
          userIsTeacher
          school {
            id
            name
          }
          yearGroup {
            id
            yearGroup
          }
          educationLevel {
            id
            level
          }
          subject {
            id
            name
            subjectAndChildren {
              id
              name
              feedbackFrameworks {
                educationLevel {
                  level
                }
              }
            }
          }
          academicYear {
            id
            name
          }
          essaysMarkedCount
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;
export const GET_TEACHERS = gql`
  query {
    teachers {
      id
      user {
        id
        fullName
      }
      school {
        name
      }
    }
    schools {
      id
      name
    }
  }
`;
export const GET_TEACHER = gql`
  query ($teacher: ID!) {
    teacher(teacher: $teacher) {
      id
      classes {
        id
        name
        yearGroup {
          yearGroup
        }
        subject {
          name
        }
        academicYear {
          id
          name
        }
        educationLevel {
          level
        }
      }
      user {
        id
        fullName
      }
      school {
        name
      }
    }
    possibleClassesForTeacher(teacher: $teacher) {
      id
      name
      yearGroup {
        yearGroup
      }
      subject {
        name
      }
      academicYear {
        id
        name
      }
      educationLevel {
        level
      }
    }
  }
`;
export const GET_STUDENT = gql`
  query ($student: ID!) {
    student(student: $student) {
      id
      studentIdentifier
      identifier
      pseudonymisedAt
      firstName
      lastName
      user {
        email
      }
      classes {
        id
        name
        yearGroup {
          yearGroup
        }
        subject {
          name
        }
        academicYear {
          id
          name
        }
        educationLevel {
          level
        }
      }
      school {
        name
      }
      yearGroup {
        yearGroup
        id
      }
    }
    possibleClassesForStudent(student: $student) {
      id
      name
      yearGroup {
        yearGroup
      }
      subject {
        name
      }
      educationLevel {
        level
      }
      academicYear {
        id
        name
      }
    }
  }
`;
export const GET_STUDENTS = gql`
  query {
    students {
      id
      studentIdentifier
      identifier
      pseudonymisedAt
      school {
        id
        name
      }
      yearGroup {
        id
        yearGroup
      }
    }
    schools {
      id
      name
    }
    yearGroups {
      id
      yearGroup
    }
    possibleClasses {
      id
      name
      school {
        name
      }
      yearGroup {
        yearGroup
      }
      academicYear {
        id
        name
      }
    }
  }
`;

export const GET_SCHOOL_CLASS_DATA = gql`
  query ($schoolClass: ID!) {
    schoolClass(schoolClass: $schoolClass) {
      id
      name
      yearGroup {
        id
        yearGroup
      }
      stats {
        assignmentsCreatedStats {
          current
          previous
        }
        answersMarkedStats {
          current
          previous
        }
      }
      educationLevel {
        id
        level
        yearGroups {
          id
          yearGroup
        }
      }
      subject {
        id
        name
        subjectAndChildren {
          id
          name
        }
      }
      academicYear {
        id
        name
      }
      essaysMarkedCount
      teachers {
        id
        school {
          name
        }
        user {
          fullName
        }
      }
      students {
        id
        studentIdentifier
        identifier
        pseudonymisedAt
        yearGroup {
          yearGroup
        }
      }
    }
    possibleStudents(schoolClass: $schoolClass) {
      id
      identifier
      pseudonymisedAt
    }
    possibleTeachers(schoolClass: $schoolClass) {
      id
      user {
        id
        fullName
      }
    }
  }
`;

export const SEARCH_SCHOOLS = gql`
  query (
    $searchString: String!
    $baseSearch: Boolean
    $subscribedOnly: Boolean
    $subscriptionApprovalStatus: String
    $schoolGroup: ID
    $phases: [String!]
    $localAuthority: ID
    $first: Int
    $after: String
  ) {
    matchedSchools(
      searchString: $searchString
      baseSearch: $baseSearch
      subscribedOnly: $subscribedOnly
      subscriptionApprovalStatus: $subscriptionApprovalStatus
      schoolGroup: $schoolGroup
      phases: $phases
      localAuthority: $localAuthority
      first: $first
      after: $after
    ) {
      edges {
        cursor
        node {
          id
          name
          addressLine1
          town
          phaseOfEducation
          phaseOfEducationDisplay
          identifiedContactsCount
          lastEnrichedAt
          localAuthority {
            id
            code
            name
          }
          subscriptionStatus
          subscription {
            isSchoolApproved
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;
export const GET_SCHOOL_PHASE_OPTIONS = gql`
  query SchoolPhaseOptions {
    schoolPhaseOptions {
      value
      label
      count
    }
  }
`;
export const SEARCH_LOCAL_AUTHORITIES = gql`
  query (
    $searchString: String!
    $baseSearch: Boolean
    $first: Int
    $after: String
  ) {
    matchedLocalAuthorities(
      searchString: $searchString
      baseSearch: $baseSearch
      first: $first
      after: $after
    ) {
      edges {
        cursor
        node {
          id
          code
          name
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;
export const SEARCH_SCHOOL_GROUPS = gql`
  query (
    $searchString: String!
    $baseSearch: Boolean
    $subscribedOnly: Boolean
    $first: Int
    $after: String
  ) {
    matchedSchoolGroups(
      searchString: $searchString
      baseSearch: $baseSearch
      subscribedOnly: $subscribedOnly
      first: $first
      after: $after
    ) {
      edges {
        cursor
        node {
          id
          name
          schoolCount
          isSubscribed
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_SCHOOL = gql`
  query ($school: ID!) {
    school(school: $school) {
      id
      name
      town
      subscriptionStatus
      isSubscribed
      studentsCount
      teachersCount
      wondeId
      wondeLastSynced
      emailDomains {
        domain
      }
      subscription {
        id
        defaultTier
        numberOfLicencesAllowed
        actualNumberOfLicences
        isSchoolApproved
        yearGroupLicences {
          id
          numberOfLicencesAllowed
          actualNumberOfLicences
          tier
          subjects {
            id
            name
          }
          yearGroup {
            id
            yearGroup
          }
        }
      }
      syncs {
        id
        lastUpdated
        needsAction
        inProgress
        completed
        syncStatus
      }
      classes {
        id
        name
        isActive
        wondeId
        wondeNeedsReview
        wondeLastSynced
        yearGroup {
          id
          yearGroup
        }
        subject {
          id
          name
        }
      }
    }
  }
`;

export const GET_FILTERED_SCHOOL_CLASSES_SUPERUSER = gql`
  query (
    $school: ID!
    $searchTerm: String
    $wondeState: String
    $yearGroup: ID
    $academicYear: ID
    $first: Int
    $after: String
  ) {
    filteredSchoolClassesSuperuser(
      school: $school
      searchTerm: $searchTerm
      wondeState: $wondeState
      yearGroup: $yearGroup
      academicYear: $academicYear
      first: $first
      after: $after
    ) {
      edges {
        cursor
        node {
          id
          name
          isActive
          wondeId
          wondeNeedsReview
          wondeLastSynced
          academicYear {
            id
            name
          }
          yearGroup {
            id
            yearGroup
          }
          subject {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_SCHOOL_GROUP = gql`
  query ($schoolGroup: ID!) {
    schoolGroup(schoolGroup: $schoolGroup) {
      id
      name
      schoolCount
      subscribedSchoolsCount
      teachersCount
      studentsCount
    }
  }
`;

export const GET_ACADEMIC_YEARS = gql`
  query {
    academicYears {
      id
      name
      isCurrent
    }
  }
`;

export const GET_ACCOUNT_SCHOOL_SUBSCRIPTION = gql`
  query AccountSchoolSubscription {
    accountSchoolSubscription {
      schoolName
      revision
      licences {
        yearGroup {
          id
          yearGroup
        }
        subjects {
          id
          name
          isActive
        }
      }
    }
  }
`;
